import { Download, MoveHorizontal, Pencil, PlayCircle } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { findReferenceElement } from '../../lib/explanationRefs';
import { downloadTextFile } from '../../lib/export';
import { useDiagramViewStore } from '../../store/useDiagramViewStore';
import Markdown, { type ReferenceHandler } from './Markdown';

interface Props {
  value: string;
  onChange: (value: string) => void;
  placeholder: string;
  /** Used for the downloaded file name. */
  title: string;
  /** Called when a reader clicks a reference link ([name](#table:name), #node:..., ...). */
  onReference?: ReferenceHandler;
  /** Architecture diagrams: how many "Step N:" story steps the explanation has, and how to play them (from a 0-based step). */
  storySteps?: number;
  onPlayStory?: (startIndex: number) => void;
}

/**
 * The written explanation of a diagram: what it shows, why it is shaped this way, and anything a reader needs to know.
 * It is Markdown. Readers see it rendered, with links that jump to the exact group, table, field or node it mentions;
 * people who may edit the diagram can switch to a Markdown editor with a live preview.
 */
export default function DiagramExplanation({ value, onChange, placeholder, title, onReference, storySteps = 0, onPlayStory }: Props) {
  const readOnly = useDiagramViewStore((s) => s.readOnly);
  const wide = useDiagramViewStore((s) => s.panelWide);
  const togglePanelWide = useDiagramViewStore((s) => s.togglePanelWide);
  const hasContent = value.trim().length > 0;
  const [editing, setEditing] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const request = useDiagramViewStore((s) => s.explanationRequest);

  // "Go to explanation" from the canvas menu: scroll to the spot that mentions the item and flash it briefly.
  useEffect(() => {
    if (!request) return;
    const { clearExplanationRequest, showToast } = useDiagramViewStore.getState();
    const link = rootRef.current ? findReferenceElement(rootRef.current, request.steps) : null;
    clearExplanationRequest();
    if (!link) {
      showToast('The explanation does not mention this');
      return;
    }
    const block = link.closest<HTMLElement>('li, tr, p, blockquote, [role="heading"]') ?? link;
    block.scrollIntoView({ behavior: 'smooth', block: 'center' });
    block.classList.remove('explanation-flash');
    void block.offsetWidth; // restart the animation if it was already running
    block.classList.add('explanation-flash');
    window.setTimeout(() => block.classList.remove('explanation-flash'), 2600);
  }, [request]);

  const download = () => downloadTextFile(`${title.replace(/[^\w.-]+/g, '-')}.md`, value, 'text/markdown');

  const playStep = onPlayStory && storySteps > 0 ? (index: number) => onPlayStory(index) : undefined;

  const readingTools = (
    <div className="mb-3 flex flex-wrap items-center gap-1">
      {playStep && (
        <button onClick={() => playStep(0)} className="btn-primary !px-3 !py-1.5 text-xs" title="Play the diagram's story from the beginning, step by step">
          <PlayCircle className="h-4 w-4" /> Play the story · {storySteps} steps
        </button>
      )}
      <button onClick={togglePanelWide} className="btn-ghost !px-2 !py-1 text-xs" title={wide ? 'Make the panel narrower' : 'Make the panel wider for reading'}>
        <MoveHorizontal className="h-3.5 w-3.5" /> {wide ? 'Narrower' : 'Wider'}
      </button>
      <button onClick={download} className="btn-ghost !px-2 !py-1 text-xs" title="Download this explanation as a Markdown (.md) file">
        <Download className="h-3.5 w-3.5" /> Download .md
      </button>
    </div>
  );

  if (readOnly) {
    return hasContent ? (
      <div ref={rootRef}>
        {readingTools}
        <Markdown source={value} onReference={onReference} onPlayStep={playStep} toc />
      </div>
    ) : (
      <p className="text-sm text-gray-400 dark:text-gray-500">No explanation has been written for this diagram yet.</p>
    );
  }

  if (!editing && hasContent) {
    return (
      <div ref={rootRef}>
        {readingTools}
        <Markdown source={value} onReference={onReference} onPlayStep={playStep} toc />
        <button onClick={() => setEditing(true)} className="btn-ghost mt-3 !py-1.5 text-sky-600 dark:text-sky-300">
          <Pencil className="h-3.5 w-3.5" /> Edit explanation
        </button>
      </div>
    );
  }

  return (
    <div ref={rootRef}>
      <textarea
        autoFocus={editing}
        className="w-full rounded-lg border border-gray-300 bg-white px-3 py-2 font-mono text-xs leading-relaxed shadow-sm placeholder:text-gray-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/15 dark:border-white/15 dark:bg-gray-800 dark:text-gray-100"
        rows={18}
        placeholder={placeholder}
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
      <p className="mt-1.5 text-xs text-gray-400 dark:text-gray-500">
        Markdown: <code># heading</code>, <code>**bold**</code>, <code>- list</code>, <code>`code`</code>, <code>[link](https://…)</code>, tables with <code>|</code>. Link to a
        diagram item with <code>[text](#table:name)</code>, <code>#column:table.field</code>, <code>#group:name</code>, <code>#enum:name</code> or <code>#node:name</code>. Saved automatically.
      </p>
      {hasContent && (
        <>
          <div className="mb-1 mt-4 text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">Preview</div>
          <div className="rounded-lg border border-gray-200 p-3 dark:border-white/10">
            <Markdown source={value} onReference={onReference} />
          </div>
          <button onClick={() => setEditing(false)} className="btn-primary mt-3 !py-1.5">
            Done
          </button>
        </>
      )}
    </div>
  );
}
